import {
    ChangeEvent,
    KeyboardEvent,
    MouseEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import classNames from "classnames";

import Input from "@/common/Input";
import StickyButton from "@/common/StickyButton";
import { logAnalyticsEvent } from "@/constants/amplitude";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { sendMetrics } from "@/constants/gtag";
import { useDeviceType } from "@/hooks/useDeviceType";
import i18n from "@/localization/i18next";
import { QuestionComponentProps } from "@/types/common";
import { useCurrency } from "@/utils/GeolocationProvider";
import useAnswers from "@/utils/useAnswers";

const CurrencyInput = ({ handleNext, question, isActive }: QuestionComponentProps) => {
    const { isMobile, isAndroid } = useDeviceType();
    const inputRef = useRef<HTMLInputElement>(null);
    const { currencySymbol, currency } = useCurrency();

    const [value, setValue] = useState<number | null>(null);
    const [valueStr, setValueStr] = useState<string | null>(null);
    const { setAnswers } = useAnswers();

    const handleContinue = useCallback(() => {
        setAnswers((ans) => ({ ...ans, [question.id]: value }));
        handleNext();

        logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: question.event,
            [WebEventProps.RESULT]: value,
            [WebEventProps.CURRENCY]: currency,
        });
        sendMetrics(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: question.event,
            [WebEventProps.RESULT]: value,
            [WebEventProps.CURRENCY]: currency,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleNext, value]);

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const newValueStr = e.target.value || currencySymbol;
            const numberValue = newValueStr.replace(currencySymbol, "");

            if (!/^[0-9]*$/.test(numberValue.replace(/\s/gi, ""))) {
                return;
            }

            setValueStr(newValueStr);
            setValue(+numberValue || null);
        },
        [currencySymbol]
    );

    const handleFocus = useCallback(() => {
        if (!valueStr) {
            setValueStr(currencySymbol);
        }
        setTimeout(() => {
            //to first set currency in input and only then set cursor
            inputRef?.current?.setSelectionRange(currencySymbol.length, currencySymbol.length);
        }, 0);
    }, [valueStr, currencySymbol]);

    const handleBlur = useCallback(() => {
        if (!value) {
            setValueStr(null);
        }
    }, [value]);

    // prevent entering values before currency symbol
    const moveCarriageFromStringBeginning = (
        e: KeyboardEvent<HTMLInputElement> | MouseEvent<HTMLInputElement>
    ) => {
        const isCarriageMovedToStart = !e.currentTarget.selectionStart;
        if (isCarriageMovedToStart) {
            e.currentTarget.setSelectionRange(1, 1);
        }
    };

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter" && !!value) {
                handleContinue();
                inputRef?.current?.blur();
            }
        },
        [value, handleContinue]
    );

    useEffect(() => {
        const isAutofocus = !isMobile || isAndroid;

        if (isActive && isAutofocus) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 200);
        }
    }, [isActive, isMobile, isAndroid]);

    return (
        <div className="grid h-full content-between gap-10 pb-6 text-white female:text-black md:pb-10">
            <div className="grid gap-6">
                <div className="self-start text-zing-24-32 font-semibold">{question.title}</div>
                <div className="grid gap-2">
                    <Input
                        ref={inputRef}
                        value={valueStr}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onKeyUp={moveCarriageFromStringBeginning}
                        onKeyDown={handleKeyDown}
                        onMouseUp={moveCarriageFromStringBeginning}
                        placeholder={i18n.t("enter_amount")}
                        className="px-4 py-[13px] text-center text-zing-18-26 font-semibold"
                        inputMode="numeric"
                    />
                </div>
            </div>
            <StickyButton
                isDisabled={!value}
                onClick={handleContinue}
                className={classNames({
                    visible: value,
                    hidden: !value,
                })}
            />
        </div>
    );
};

export default CurrencyInput;
